/**
 * Bounded backoff for the WebRTC (re)connect loop in VideoPlayer.
 *
 * When a camera's session can't be established (server wedged, relay tunnel
 * down, provider-create failing), connect() retries on a timer. Left alone
 * that shows the loading spinner forever with no explanation. This decides,
 * from the count of consecutive failed connects, how long to wait before the
 * next auto-retry and whether the user should now see a "can't connect" error
 * instead of a still-plausibly-transient spinner.
 *
 * We never stop auto-retrying (a live camera should recover on its own once
 * the backend does), but the interval backs off so a persistent outage isn't
 * hammered, and the error surfaces quickly so the operator isn't staring at a
 * spinner that will never resolve.
 */

// Delay (ms) before the Nth auto-retry: 5s, 5s, 15s, 30s, then capped at 30s.
import type { NonEmpty } from '@/types/common';

export const RETRY_DELAYS_MS: NonEmpty<number> = [5000, 5000, 15000, 30000];

// Show the "can't connect" error from this many consecutive failures onward.
// The first failure stays a spinner — a single dropped handshake usually
// recovers on the immediate retry and an error flash would be noise.
export const ERROR_AFTER_ATTEMPTS = 2;

interface RetryPlan {
    delayMs: number;
    shouldShowError: boolean;
}

/** `attempt` is the consecutive failed connects so far; below 1 counts as 1. */
export function getConnectionRetryPlan(attempt: number): RetryPlan {
    const n = attempt >= 1 ? attempt : 1;
    const idx = Math.min(n - 1, RETRY_DELAYS_MS.length - 1);
    return {
        delayMs: RETRY_DELAYS_MS[idx] ?? RETRY_DELAYS_MS[0],
        shouldShowError: n >= ERROR_AFTER_ATTEMPTS,
    };
}
